// The main tabs, in order, and which of them a caller is offered.
//
// Scout, Activity, the accounts tab, All accounts, Sprints, then My Style, Review and Admin for the roles
// that see them (Vikram, go-live). The accounts tab, by role:
//   - strategist                      My Accounts on `/execution`: the board, on their own accounts
//   - admin, leadership, viewer       Strategist Accounts on `/execution`: every strategist's
//   - ae                              My Accounts on `/ae`: their own book. An AE has never been
//                                     offered the board (`/execution` sends them to `/ae`).
// Before `/api/me` answers no tab shows (GRO-491): the bar fills in once, not in two steps. `/accounts` (the old oversight page)
// redirects to the board. The queue folded into All accounts (once Available, whose `/available`
// and `/admin/queue` still resolve and redirect there).
//
// Pure, and imported by `node --test`.

import type { Viewer } from '../api/types';

export interface ViewTab {
  to: string;
  label: string;
  hint: string;
}

export const VIEW_TABS: ViewTab[] = [
  { to: '/scout', label: 'Scout', hint: 'Ask anything — not one account' },
  { to: '/activity', label: 'Activity', hint: 'How outbound is going' },
  // The accounts slot, relabelled per role by `accountsTab` below.
  { to: '/execution', label: 'My Accounts', hint: 'What to work next' },
  { to: '/all-accounts', label: 'All accounts', hint: 'Every account, who owns it, and what is available' },
  // The weekly sprint check-in (Deborah's Q4 plan): a sprint's accounts and totals.
  { to: '/sprints', label: 'Sprints', hint: 'How each outbound sprint is going' },
  // Settings → My style (W7, #413): also in the profile menu; one page, two ways in.
  { to: '/settings/style', label: 'My Style', hint: 'How Scout writes in your voice' },
  { to: '/admin/sends', label: 'Review', hint: 'Auto-lane sequences waiting for approval' },
  { to: '/admin', label: 'Admin', hint: 'Everything Scout has done' },
];

// The accounts slot for the roles that read every strategist's board, and for an AE.
const STRATEGIST_ACCOUNTS_TAB: ViewTab = {
  to: '/execution', label: 'Strategist Accounts', hint: 'Every strategist’s accounts, with a Strategist filter',
};
const AE_ACCOUNTS_TAB: ViewTab = {
  to: '/ae', label: 'My Accounts', hint: 'Every account you own, opportunities and meetings first',
};
const ALL_STRATEGISTS_ROLES = new Set(['admin', 'leadership', 'viewer']);

function accountsTab(slot: ViewTab, viewer: Viewer | undefined): ViewTab {
  if (viewer?.role === 'ae') return AE_ACCOUNTS_TAB;
  if (roleIn(viewer, ALL_STRATEGISTS_ROLES)) return STRATEGIST_ACCOUNTS_TAB;
  return slot;
}

// The roles each role-limited tab is for. The grants below still have to agree; these only stop a
// role being offered a page that is not for it (a leader's My Style).
const STYLE_ROLES = new Set(['strategist', 'admin']);

function roleIn(viewer: Viewer | undefined, roles: Set<string>): boolean {
  return Boolean(viewer?.role && roles.has(viewer.role));
}

// Which tabs this caller is offered. Visibility only — every route behind each board decides
// for itself, and the admin console refuses `leadership` on the grant rather than on this.
//
// All accounts and Sprints are offered to every role once `/api/me` has answered (go-live): every
// role reads the whole book (BOOK_VIEW). What each may do there comes from the capability flags, not the tab.
export function viewIsOffered(to: string, viewer: Viewer | undefined): boolean {
  if (to === '/all-accounts' || to === '/sprints') return Boolean(viewer?.role);
  if (to === '/scout') return Boolean(viewer?.sees_scout_tab);
  if (to === '/settings/style') return roleIn(viewer, STYLE_ROLES) && Boolean(viewer?.sees_scout_tab);
  // The auto lane's review queue (W9): whoever may approve, admin and leadership.
  if (to === '/admin/sends') return Boolean(viewer?.can_approve);
  if (to === '/admin') return Boolean(viewer?.sees_admin_view);
  return true;
}

/** The tabs this caller sees, in order. None until the viewer is known (GRO-491). */
export function offeredViewTabs(viewer: Viewer | undefined): ViewTab[] {
  if (!viewer) return [];
  return VIEW_TABS
    .filter(tab => viewIsOffered(tab.to, viewer))
    .map(tab => (tab.to === '/execution' ? accountsTab(tab, viewer) : tab));
}
